/**
 * Editor model → compiled RRULE / RDATE / EXDATE / scheduleClass.
 *
 * One-way and stable: the same editor model always compiles to the same
 * strings (docs/plans/planned-task-schedule.md §2, §3). RRULE is never
 * parsed back — the editor model is authoritative.
 *
 * No `rrule` import here: the RRULE text is assembled directly so every §3
 * matrix row produces the exact documented string (component order pinned).
 */

import { ScheduleCalendarRule, ScheduleDatesRule, ScheduleRule, ScheduleEnds, ScheduleNth, ScheduleWeekCycle, ScheduleWeekday } from './schedule.rule';
import { CompiledTaskSchedule, ScheduleClass, ScheduleExceptions } from './schedule.types';

/** ISO weekday (1 = Monday … 7 = Sunday) → iCal BYDAY token. */
const WEEKDAY_TOKENS: readonly string[] = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

const WEEKDAYS_MON_FRI: readonly ScheduleWeekday[] = [1, 2, 3, 4, 5];
const WEEKDAYS_MON_SAT: readonly ScheduleWeekday[] = [1, 2, 3, 4, 5, 6];
const WEEKDAYS_WEEKEND: readonly ScheduleWeekday[] = [6, 7];

export function weekdayToken(weekday: ScheduleWeekday): string {

    return WEEKDAY_TOKENS[weekday - 1];
}

/** Sorted, deduped ISO weekday list; empty input falls back to Monday so a rule is always well formed. */
function normaliseDays(days: readonly ScheduleWeekday[] | undefined): ScheduleWeekday[] {

    const unique = Array.from(new Set(days ?? []));

    return unique.length > 0 ? unique.sort((a, b) => a - b) : [1];
}

function byDayList(days: readonly ScheduleWeekday[] | undefined): string {

    return normaliseDays(days)
        .map(weekdayToken)
        .join(',');
}

function byMonthList(months: readonly number[] | undefined): string | undefined {

    if (!months || months.length === 0) {
        return undefined;
    }

    const unique = Array.from(new Set(months)).sort((a, b) => a - b);

    return unique.join(',');
}

/** UNTIL as a date-only iCal value; occurrences expand at fake-UTC midnight, so the boundary date stays inclusive. */
function untilStamp(date: string): string {

    return date.replace(/-/g, '');
}

function endsSuffix(ends: ScheduleEnds | undefined): string {

    if (!ends || ends.type === 'never') {
        return '';
    }

    if (ends.type === 'onDate') {
        return `;UNTIL=${untilStamp(ends.date)}`;
    }

    return `;COUNT=${ends.count}`;
}

function dailyRule(calendar: ScheduleCalendarRule): string {

    const preset = calendar.dailyPreset ?? 'everyDay';

    // The working-day preset has no static rule (see compileSchedule). It is filtered out before
    // this function is reached; the fallback keeps a direct compilePattern() call well formed
    // rather than emitting a rule that would expand the wrong days.
    if (preset === 'workingDays') {
        return `FREQ=WEEKLY;BYDAY=${byDayList(WEEKDAYS_MON_FRI)}`;
    }

    if (preset === 'weekdays') {
        return `FREQ=WEEKLY;BYDAY=${byDayList(WEEKDAYS_MON_FRI)}`;
    }

    if (preset === 'sixDay') {
        return `FREQ=WEEKLY;BYDAY=${byDayList(WEEKDAYS_MON_SAT)}`;
    }

    if (preset === 'weekend') {
        return `FREQ=WEEKLY;BYDAY=${byDayList(WEEKDAYS_WEEKEND)}`;
    }

    const interval = preset === 'everyNDays' ? (calendar.dailyInterval ?? 2) : 1;

    return `FREQ=DAILY;INTERVAL=${interval}`;
}

function weeklyRule(calendar: ScheduleCalendarRule, fixedInterval?: number): string {

    const interval = fixedInterval ?? calendar.weeklyInterval ?? 1;

    return `FREQ=WEEKLY;INTERVAL=${interval};BYDAY=${byDayList(calendar.daysOfWeek)}`;
}

/** Highest ISO week number any year can carry; week 53 simply yields nothing in 52-week years. */
const MAX_ISO_WEEK = 53;

/**
 * ISO week-of-year parity rule (government contracts).
 *
 * Unlike A/B — which counts a strict fortnight forward from DTSTART — parity
 * follows the wall calendar, so BYWEEKNO enumerates every odd (or even) ISO
 * week. Across a 53-week ISO year an odd schedule therefore runs two weeks
 * running (week 53 then week 1) and an even schedule skips two. That is the
 * point of the mode, not a defect to correct.
 */
function isoParityRule(calendar: ScheduleCalendarRule, parity: 'isoOdd' | 'isoEven'): string {

    const wanted = parity === 'isoOdd' ? 1 : 0;
    const weeks: number[] = [];

    for (let week = 1; week <= MAX_ISO_WEEK; week += 1) {
        if (week % 2 === wanted) {
            weeks.push(week);
        }
    }

    return `FREQ=YEARLY;BYWEEKNO=${weeks.join(',')};BYDAY=${byDayList(calendar.daysOfWeek)}`;
}

/** Fortnightly splits two ways: A/B keeps a strict fortnight, isoOdd/isoEven follows calendar parity. */
function fortnightlyRule(calendar: ScheduleCalendarRule): string {

    const selector = calendar.weekSelector;

    if (selector === 'isoOdd' || selector === 'isoEven') {
        return isoParityRule(calendar, selector);
    }

    return weeklyRule(calendar, 2);
}

/** Month-day ordinal: 'last' becomes -1 so it sorts (and emits) after every calendar day. */
function monthDayOrdinal(day: number | 'last'): number {

    return day === 'last' ? -1 : day;
}

/** Ascending, with 'last' (-1) pushed to the end rather than the front. */
function compareMonthOrdinals(a: number, b: number): number {

    if (a < 0 && b >= 0) {
        return 1;
    }

    if (b < 0 && a >= 0) {
        return -1;
    }

    return a - b;
}

/** BYMONTHDAY list, e.g. [1, 15] → '1,15' and ['last'] → '-1'. */
function byMonthDayList(daysOfMonth: readonly (number | 'last')[] | undefined): string {

    const ordinals = (daysOfMonth ?? [1]).map(monthDayOrdinal);
    const unique = Array.from(new Set(ordinals.length > 0 ? ordinals : [1])).sort(compareMonthOrdinals);

    return unique.join(',');
}

/** BYDAY list for nth-weekday rules, e.g. nths [1, 3] + Monday → '1MO,3MO'. */
function byNthWeekdayList(nths: readonly ScheduleNth[] | undefined, weekday: ScheduleWeekday): string {

    const ordinals = (nths ?? [1]).map(nth => (nth === 'last' ? -1 : nth));
    const unique = Array.from(new Set(ordinals.length > 0 ? ordinals : [1])).sort(compareMonthOrdinals);

    return unique
        .map(ordinal => `${ordinal}${weekdayToken(weekday)}`)
        .join(',');
}

/** Shared monthly/quarterly/seasonal-monthly body: BYMONTHDAY= or BYDAY=nTH. */
function monthlyOnClause(calendar: ScheduleCalendarRule): string {

    if (calendar.monthlyOn === 'nthWeekday') {
        return `BYDAY=${byNthWeekdayList(calendar.nths, calendar.weekday ?? 1)}`;
    }

    return `BYMONTHDAY=${byMonthDayList(calendar.daysOfMonth)}`;
}

function monthlyRule(calendar: ScheduleCalendarRule, interval: number, fixedMonthsAsYearly = false): string {

    const months = byMonthList(calendar.months);

    if (months && fixedMonthsAsYearly) {
        return `FREQ=YEARLY;BYMONTH=${months};${monthlyOnClause(calendar)}`;
    }

    if (months) {
        return `FREQ=MONTHLY;${monthlyOnClause(calendar)};BYMONTH=${months}`;
    }

    return `FREQ=MONTHLY;INTERVAL=${interval};${monthlyOnClause(calendar)}`;
}

function yearlyRule(calendar: ScheduleCalendarRule): string {

    const months = byMonthList(calendar.months) ?? '1';

    return `FREQ=YEARLY;BYMONTH=${months};${monthlyOnClause(calendar)}`;
}

/** Seasonal months on a weekly/daily repeat become BYMONTH on the same rule. */
function withSeason(rule: string, calendar: ScheduleCalendarRule): string {

    const months = byMonthList(calendar.months);

    return months ? `${rule};BYMONTH=${months}` : rule;
}

/** Pattern body without ends — the shape the scheduleClass whitelist and describe() reason about. */
export function compilePattern(calendar: ScheduleCalendarRule): string {

    switch (calendar.repeat) {
        case 'daily':
            return withSeason(dailyRule(calendar), calendar);
        case 'weekly':
            return withSeason(weeklyRule(calendar), calendar);
        case 'fortnightly':
            return withSeason(fortnightlyRule(calendar), calendar);
        case 'monthly':
            return monthlyRule(calendar, calendar.monthInterval ?? 1);
        case 'quarterly':
            return monthlyRule(calendar, calendar.monthInterval ?? 3, true);
        case 'yearly':
            return yearlyRule(calendar);
    }
}

export function compileScheduleRule(calendar: ScheduleCalendarRule): string {

    return `${compilePattern(calendar)}${endsSuffix(calendar.ends)}`;
}

/**
 * DTSTART for a calendar pattern. Fortnightly week 'A' anchors on the week of
 * effectiveFrom; week 'B' shifts the anchor forward by 7 days so the two
 * selectors interleave on opposite weeks (plan §3.2).
 *
 * The isoOdd/isoEven selectors are deliberately unshifted: their BYWEEKNO rule
 * is anchored to the wall calendar, so DTSTART only clips the start and must
 * never move the parity.
 */
export function compileAnchor(calendar: ScheduleCalendarRule, effectiveFrom: string): string {

    if (calendar.repeat === 'fortnightly' && calendar.weekSelector === 'B') {
        return addDays(effectiveFrom, 7);
    }

    return effectiveFrom;
}

/**
 * Which half of an anchored fortnight a local date falls in, counted from the effective-from day `compileAnchor`
 * starts week A on. Fortnightly A/B compiles to `FREQ=WEEKLY;INTERVAL=2` with no WKST, so rrule counts whole
 * Monday-started weeks from DTSTART: the week holding effectiveFrom and every second week either side of it is
 * A, the weeks between are B. Planned-work boards label dates with this so the label and the dates the engine
 * emits cannot disagree; counting from anything else (ISO week numbers, the calendar year) drifts at 53-week years.
 */
export function fortnightWeekCycle(date: string, effectiveFrom: string): ScheduleWeekCycle {

    const weeks = Math.round((epochDay(mondayOf(date)) - epochDay(mondayOf(effectiveFrom))) / 7);
    return Math.abs(weeks) % 2 === 0 ? 'A' : 'B';
}

/** The Monday that starts a date's week; rrule's default WKST, which A/B week counting rides on. */
function mondayOf(date: string): string {

    return addDays(date, 1 - isoWeekday(date));
}

/** Whole days since 1970-01-01 for a local date string; only differences are ever read. */
function epochDay(date: string): number {

    const [year, month, day] = date.split('-').map(Number);
    return Date.UTC(year, month - 1, day) / 86400000;
}

/**
 * Whether a calendar pattern is the dynamic working-day one.
 *
 * Kept as a predicate rather than inlined because three call sites need the same question answered
 * — the class stamp, the rule compile (which must emit nothing), and the expansion path.
 */
export function isWorkingDaysCalendar(calendar: ScheduleCalendarRule | undefined): boolean {

    return calendar?.repeat === 'daily' && calendar.dailyPreset === 'workingDays';
}

/**
 * §2 whitelist: class evaluation needs kind calendar, a preset that lands on
 * 5–7 of every 7 days, and a policy that does not shift dates.
 *
 * An ABSENT schedule is the working-day default (plan §2 "Absent schedule = due every working
 * day"), so it stamps the dynamic 'workingDays' class before any of the calendar rules run.
 *
 * The 'dates' kind never carries a class, and the reason is the same one that keeps sparse calendar
 * patterns out: a class means "due by default on every applicable day, minus excludes", and an
 * explicit list of at most two dozen days has no default density to subtract from. Its occurrences
 * are materialised into DueDay.include like every other non-class schedule.
 */
export function compileScheduleClass(editor: ScheduleRule | undefined): ScheduleClass | undefined {

    if (!editor) {
        return 'workingDays';
    }

    if (editor.kind !== 'calendar') {
        return undefined;
    }

    const calendar = editor.calendar;

    if (!calendar || calendar.repeat !== 'daily') {
        return undefined;
    }

    // Checked BEFORE the policy guard, which exists because a shift needs the expand step and the
    // class path would answer with the unshifted date. That reasoning does not reach here: a
    // working-day task lands on a working day by construction, so every policy is the identity and
    // none of them can disagree with the class evaluation.
    if (calendar.dailyPreset === 'workingDays') {
        return 'workingDays';
    }

    if (calendar.nonWorkingDayPolicy !== 'keep' && calendar.nonWorkingDayPolicy !== 'skip') {
        return undefined;
    }

    if (calendar.months && calendar.months.length > 0) {
        return undefined;
    }

    const preset = calendar.dailyPreset ?? 'everyDay';

    if (preset === 'everyDay') {
        return (calendar.dailyInterval ?? 1) === 1 ? 'daily' : undefined;
    }

    if (preset === 'weekdays') {
        return 'weekdays';
    }

    if (preset === 'sixDay') {
        return 'sixDay';
    }

    return undefined;
}

function sortedUnique(dates: readonly string[]): string[] {

    return Array.from(new Set(dates)).sort();
}

/** Inclusive day-by-day expansion of a blackout range into EXDATEs. */
export function expandRange(from: string, to: string): string[] {

    if (from > to) {
        return [];
    }

    const dates: string[] = [];
    let cursor = from;

    while (cursor <= to) {
        dates.push(cursor);
        cursor = addDays(cursor, 1);
    }

    return dates;
}

/** Pure calendar-string date arithmetic; no JS Date involved. */
export function addDays(date: string, days: number): string {

    const [year, month, day] = date.split('-').map(Number);
    let y = year;
    let m = month;
    let d = day + days;

    while (d > daysInMonth(y, m)) {
        d -= daysInMonth(y, m);
        m += 1;

        if (m > 12) {
            m = 1;
            y += 1;
        }
    }

    while (d < 1) {
        m -= 1;

        if (m < 1) {
            m = 12;
            y -= 1;
        }

        d += daysInMonth(y, m);
    }

    return formatDate(y, m, d);
}

export function daysInMonth(year: number, month: number): number {

    if (month === 2) {
        return isLeapYear(year) ? 29 : 28;
    }

    return month === 4 || month === 6 || month === 9 || month === 11 ? 30 : 31;
}

export function isLeapYear(year: number): boolean {

    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

export function formatDate(year: number, month: number, day: number): string {

    return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** ISO weekday (1 = Monday … 7 = Sunday) from a local date string, via Zeller-style day counting. */
export function isoWeekday(date: string): ScheduleWeekday {

    const [year, month, day] = date.split('-').map(Number);
    const shiftedMonth = month < 3 ? month + 12 : month;
    const shiftedYear = month < 3 ? year - 1 : year;
    const century = Math.floor(shiftedYear / 100);
    const yearOfCentury = shiftedYear % 100;
    const h = (day + Math.floor((13 * (shiftedMonth + 1)) / 5) + yearOfCentury + Math.floor(yearOfCentury / 4) + Math.floor(century / 4) + 5 * century) % 7;

    // Zeller: 0 = Saturday … 6 = Friday. Map onto ISO 1 = Monday … 7 = Sunday.
    return (((h + 5) % 7) + 1) as ScheduleWeekday;
}

/** Collect the EXDATE set implied by task-level skips (single dates and ranges). */
export function collectSkipDates(exceptions: ScheduleExceptions | undefined): string[] {

    const skips = [...(exceptions?.skips ?? []), ...(exceptions?.lineBlackouts ?? []), ...(exceptions?.planBlackouts ?? [])];
    const dates: string[] = [];

    for (const skip of skips) {
        if (skip.date) {
            dates.push(skip.date);
            continue;
        }

        if (skip.from && skip.to) {
            dates.push(...expandRange(skip.from, skip.to));
        }
    }

    return sortedUnique(dates);
}

export function collectExtraDates(exceptions: ScheduleExceptions | undefined): string[] {

    return sortedUnique((exceptions?.extras ?? []).map(extra => extra.date));
}

/**
 * The canonical occurrence list for a 'dates' schedule: sorted ascending and deduplicated.
 *
 * Sorting here rather than trusting the stored order keeps evaluation independent of how a caller
 * happened to write the list — the guard canonicalises on the way in, but a document written before
 * that guard existed, or by a migration, must still evaluate in date order.
 */
export function normaliseScheduleDates(dates: ScheduleDatesRule | undefined): string[] {

    return sortedUnique(dates?.dates ?? []);
}

/**
 * Editor model (+ optional exception set) → the compiled artifacts persisted on the Task.
 *
 * An ABSENT editor model is legal and meaningful: it is the working-day default, and it compiles to
 * a bare `{ scheduleClass: 'workingDays' }` — no rule, no dates. So does the explicit 'workingDays'
 * daily preset. NO RRULE IS EMITTED FOR EITHER, and that is deliberate rather than an omission: the
 * day set is `ctx.workWeek`, which is resolved per line at evaluation time, so any static rule this
 * function could write (BYDAY=MO,TU,WE,TH,FR say) would be wrong for the first tenant that runs a
 * Sun–Thu week — and silently wrong, because the rule would still expand.
 *
 * A 'dates' schedule likewise emits NO RRULE, for the opposite reason: there is no pattern to
 * express. Its listed days compile into `extraDates`, which is precisely what an RDATE set is — a
 * bare list of occurrence dates with no rule behind it — so they merge with any exception extras.
 */
export function compileSchedule(editor: ScheduleRule | undefined, exceptions?: ScheduleExceptions): CompiledTaskSchedule {

    const compiled: CompiledTaskSchedule = {};

    if (editor?.kind === 'calendar' && editor.calendar && !isWorkingDaysCalendar(editor.calendar)) {
        compiled.scheduleRule = compileScheduleRule(editor.calendar);
    }

    const listedDates = editor?.kind === 'dates' ? normaliseScheduleDates(editor.dates) : [];
    const extraDates = sortedUnique([...listedDates, ...collectExtraDates(exceptions)]);
    const exdates = collectSkipDates(exceptions);

    if (extraDates.length > 0) {
        compiled.extraDates = extraDates;
    }

    if (exdates.length > 0) {
        compiled.exdates = exdates;
    }

    const scheduleClass = compileScheduleClass(editor);

    if (scheduleClass) {
        compiled.scheduleClass = scheduleClass;
    }

    return compiled;
}
